/**
 * Runtime configuration module.
 *
 * Instead of baking environment values into the JS bundle at build time
 * (via Vite's `import.meta.env`), this module loads a JSON config file
 * at runtime so that the **same build artifact** can be deployed to any
 * environment by simply swapping the config file.
 *
 * Usage:
 *   await loadRuntimeConfig();          // call once at app startup
 *   const cfg = getRuntimeConfig();     // call anywhere afterwards
 *   cfg.VITE_API_URL                    // typed access
 */

export interface RuntimeConfig {
  VITE_APP_BASE_URL: string;
  VITE_API_URL: string;
  VITE_AUTH_URL: string;
  VITE_API_PATH: string;
  VITE_APP_NAME: string;
  VITE_API_TIMEOUT: string;
  VITE_NODE_ENV: string;
  VITE_LOG_LEVEL: string;
  VITE_AUTH_CODE_URL: string;
  VITE_ACCESS_TOKEN_URL: string;
  VITE_REFRESH_TOKEN_URL: string;
  VITE_USE_PKCE: string;
  VITE_API_KEY: string;
  VITE_ABILITY_CACHE_PERIOD: string;
  VITE_CLOUDMERSIVE_API_KEY: string;
  VITE_CLOUDMERSIVE_SCAN_URL: string;
  VITE_VIRUS_SCAN_ENABLED: string;
  VITE_SESSION_TIMEOUT_MINUTES: string;
  VITE_CC_ALLOWED_EMAIL_DOMAINS: string;
  VITE_APP_VERSION: string;
  [key: string]: string;
}

let _config: RuntimeConfig | null = null;

/**
 * Fetch and cache the runtime configuration.
 * Must be called **before** React renders.
 *
 * In development the Vite dev-server serves `public/config.dev.json`
 * (real values) while the production build ships `config.json` whose
 * placeholder tokens are replaced at deploy time.
 */
export async function loadRuntimeConfig(): Promise<RuntimeConfig> {
  const configPath =
    import.meta.env.MODE === 'development'
      ? '/config.dev.json'
      : '/config.json';

  const response = await fetch(configPath);
  if (!response.ok) {
    throw new Error(
      `Failed to load runtime config from ${configPath}: ${response.status}`,
    );
  }
  _config = await response.json();
  return _config!;
}

/**
 * Retrieve the cached runtime configuration.
 * Throws if `loadRuntimeConfig()` has not been called yet.
 */
export function getRuntimeConfig(): RuntimeConfig {
  if (!_config) {
    throw new Error(
      'Runtime config not loaded. Call loadRuntimeConfig() before using getRuntimeConfig().',
    );
  }
  return _config;
}

/**
 * Override the cached config – useful for tests.
 */
export function setRuntimeConfig(config: RuntimeConfig): void {
  _config = config;
}
